/* eslint array-bracket-newline: 0 */

import React, { useEffect, useReducer, useCallback } from 'react'
import moment from 'moment'
import { oktaAuth } from './services/authConfig'
import './App.css'
import RouterComponent from './router/RouterComponent'
import { AuthContext } from './services/context'
import {
  getHighestRole,
  getUserProfile,
  getAssignments,
  patchUser
} from './services/apiHelper'
import { clearStorage, checkForPhoneNumber } from './services/utils'
import {
  initAmplitude,
  setAmplitudeUserId,
  sendAmplitudeData
} from './services/amplitudeHelper'
import smartlookClient from 'smartlook-client'
import * as Sentry from '@sentry/react'

if (
  process.env.REACT_APP_STAGE === 'prod' ||
  process.env.REACT_APP_STAGE === 'uat'
) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_URL,
    environment: process.env.REACT_APP_STAGE
  })
  smartlookClient.init(process.env.REACT_APP_SMARTLOOK)
}

initAmplitude()
moment.suppressDeprecationWarnings = true

const authDefault = {
  loggedIn: 'LOADING',
  permissionStatus: 'LOADING',
  userInfo: {}
}

const authReducer = (state, action) => {
  switch (action.type) {
    case 'GO_BACK': {
      return {
        ...state,
        detailView: state.lastDetailView,
        lastDetailView: state.detailView
      }
    }
    case 'SET_DETAIL_VIEW': {
      return {
        ...state,
        detailView: action.payload,
        lastDetailView: state.detailView
      }
    }
    case 'UPDATE_USER_PROFILE': {
      setAmplitudeUserId(action.payload?.user_id)
      const userInfo = checkForPhoneNumber(action.payload)
      return { ...state, userInfo }
    }
    case 'CHECK_FOR_PHONE': {
      const userInfo = checkForPhoneNumber(action.payload)
      return { ...state, userInfo }
    }
    case 'LOGIN_USER': {
      sendAmplitudeData('successful_login')
      return { ...state, loggedIn: 'SUCCESS' }
    }
    case 'LOGIN_USER_FAILED': {
      sendAmplitudeData('failed_login')
      return { ...state, loggedIn: 'FAILED' }
    }
    case 'LOG_OUT': {
      clearStorage()
      oktaAuth.signOut()
      return { ...state, loggedIn: 'FAILED', permissionStatus: 'LOADING' }
    }
    case 'UPDATE_USER_PERMISSIONS':
      return {
        ...state,
        userPermissions: action.payload,
        permissionStatus: 'LOADED'
      }
    case 'UPDATE_USER_PERMISSIONS_FAILED': {
      const permissionStatus = state.role_type ? 'LOADED' : 'FAILED'
      return { ...state, permissionStatus }
    }
    case 'SET_HIGHEST_ROLE':
      return { ...state, role_type: action.payload }
    case 'API_FAILURE':
      return { ...state }
    default:
      throw new Error()
  }
}

function App() {
  const [auth, authDispatch] = useReducer(authReducer, authDefault)
  useEffect(() => {
    const onLoad = async () => {
      const isCallback = window.location.pathname.includes('/auth-callback') ||
        (window.location.search.includes('code=') && window.location.search.includes('state='))
      if (isCallback) return
      try {
        const isAuthenticated = await oktaAuth.isAuthenticated()
        if (isAuthenticated) {
          const userData = await oktaAuth.getUser()
          const accessToken = await oktaAuth.tokenManager.get('accessToken')
          const oktaId = accessToken?.claims?.uid
          authDispatch({ type: 'LOGIN_USER', payload: userData })
          getUserProfile(oktaId, 'okta_id').then(async res => {
            let userProfile = res
            if (!userProfile && userData.email) {
              const profileByEmail = await getUserProfile(userData.email, 'email').catch(() => null)
              if (profileByEmail) {
                await patchUser({ user_id: profileByEmail.user_id, okta_id: oktaId }).catch(() => null)
                userProfile = { ...profileByEmail, okta_id: oktaId }
              }
            }
            if (userProfile) {
              authDispatch({ type: 'UPDATE_USER_PROFILE', payload: userProfile })
              getAssignments(userProfile.user_id, 'user_id').then(assignments => {
                if (assignments) {
                  const assignmentsObj = {}
                  assignments.forEach(company => {
                    assignmentsObj[company.company_id] = company.permissions
                  })
                  authDispatch({
                    type: 'UPDATE_USER_PERMISSIONS',
                    payload: assignmentsObj
                  })
                } else {
                  authDispatch({ type: 'UPDATE_USER_PERMISSIONS_FAILED' })
                }
              })
            } else {
              authDispatch({ type: 'CHECK_FOR_PHONE' })
            }
          })
          const highestRole = await getHighestRole()
          if (highestRole) {
            authDispatch({
              type: 'SET_HIGHEST_ROLE',
              payload: highestRole.role_type
            })
          }
        } else {
          authDispatch({ type: 'LOGIN_USER_FAILED' })
        }
      } catch (e) {
        console.error('FAILED TO LOAD SESSION: ', e)
        authDispatch({ type: 'LOGIN_USER_FAILED' })
      }
    }
    onLoad()
  }, [authDispatch])

  const hasAccess = useCallback(
    (companyId, permissions) => {
      const _perms =
        permissions && typeof permissions === 'string'
          ? [permissions]
          : permissions
      const _companies =
        companyId && typeof companyId === 'string' ? [companyId] : companyId

      const findPermissionInCompany = (
        availablePermissions,
        requestedPermission
      ) => {
        const matching = availablePermissions.filter(val => {
          return requestedPermission.includes(val)
        })
        if (matching.length) {
          return 'SUCCESS'
        }
        return null
      }

      try {
        if (auth.permissionStatus === 'LOADING') {
          return 'LOADING'
        }
        if (auth.role_type === 'admin') {
          return 'SUCCESS'
        }
        if (companyId === 'ANY') {
          for (let company_id in auth.userPermissions) {
            const currentCompany = findPermissionInCompany(
              auth.userPermissions[company_id],
              _perms
            )
            if (currentCompany === 'SUCCESS') {
              return 'SUCCESS'
            }
          }
          return null
        }
        if (_companies.length) {
          let found = null
          _companies.forEach(compId => {
            if (auth.userPermissions[compId]) {
              const result = findPermissionInCompany(
                auth.userPermissions[compId],
                _perms
              )
              if (result === 'SUCCESS') {
                found = 'SUCCESS'
              }
            }
          })
          return found
        }
      } catch (err) {
        return null
      }
    },
    [auth.permissionStatus, auth.role_type, auth.userPermissions]
  )

  const hasStewardAccess = useCallback(
    stewards => {
      

      try {
        if (stewards && stewards.length>0) {
          const userIsSteward = stewards.filter(s=> s.id === auth.userInfo.user_id)
          if(userIsSteward) {
            return 'SUCCESS'
          } else 
            return null
        }
      } catch (err) {
        return null
      }
    },
    [auth.userInfo.user_id]
  )

  const authObj = {
    auth,
    authDispatch,
    hasAccess,
    hasStewardAccess
  }

  return (
    <AuthContext.Provider value={authObj}>
      <div className="App" data-testid="appTestId">
        <RouterComponent />
      </div>
    </AuthContext.Provider>
  )
}

export default App
